import { HttpClient, HttpHeaders, HttpXhrBackend } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import type { CurrentUser } from '@recruitee/account-types';
import { LANGUAGE_CONFIG, LanguageConfig } from '@recruitee/i18n';
import { setDefaultTimezone } from '@recruitee/time';
import { decodeHTML } from '@recruitee/utils';

import { AuthHelpers } from './auth-helpers';
import { AuthorisationStrategy } from './authorization-strategy.interface';
import { CookieService } from './cookie';
import { HttpApiService } from './http-api.service';
import { AuthConfigI, CookieKey, IdentityHeader } from './types';
import { getQueryParam, removeQueryParam } from './url-helpers';

@Injectable()
export class AuthService {
  private readonly availableLocales: string[] = this.languageConfig.languages;
  private readonly defaultLocale: string = this.languageConfig.defaultLanguage;

  public appConfig: AuthConfigI;
  public isSaSoftLogin: boolean = false;

  private http: HttpClient;

  constructor(
    private executionStrategy: AuthorisationStrategy,
    private authHelpers: AuthHelpers,
    private cookieService: CookieService,
    @Inject(LANGUAGE_CONFIG) private languageConfig: LanguageConfig,
  ) {
    this.appConfig = this.executionStrategy.appConfig;
    this.http = new HttpClient(new HttpXhrBackend({ build: () => new XMLHttpRequest() }));
  }

  // eslint-disable-next-line max-lines-per-function
  public getAccessToken(): Promise<string> {
    // eslint-disable-next-line max-lines-per-function
    return new Promise((resolve, reject) => {
      if (getQueryParam('new_superadmin') === 'true') {
        this.authHelpers.deleteCookie(CookieKey.SuperadminAccessToken);
      }
      removeQueryParam('new_superadmin');

      const authorizationCode = getQueryParam('authorization_code');
      const accessTokenFromCookie = this.cookieService.get(CookieKey.AccessToken);
      const superadminAccessTokenFromCookie = this.cookieService.get(
        CookieKey.SuperadminAccessToken,
      );

      this.authHelpers.clearAccessTokenFromCookieBeforeExpiration();
      if (authorizationCode) {
        this.tradeAuthorizationCodeForAccessToken(authorizationCode)
          .then(data => {
            if (data.access_token) {
              this.isSaSoftLogin = true;
              resolve(data.access_token);
            } else if (data.error && Array.isArray(data.error)) {
              reject(data.error[0]);
            } else {
              reject(data);
            }
          })
          .catch(error => {
            reject(error);
          });
      } else if (this.authHelpers.isSuperadmin && superadminAccessTokenFromCookie) {
        resolve(superadminAccessTokenFromCookie);
      } else if (accessTokenFromCookie) {
        resolve(accessTokenFromCookie);
      } else {
        this.getAccessTokenWithCors().then(data => {
          if (data.access_token) {
            this.cookieService.set(CookieKey.AccessTokenExpiresAt, data.expires_at);
            resolve(data.access_token);
          } else {
            reject(data);
          }
        });
      }
    });
  }

  public clearAuthCookies(): void {
    this.authHelpers.deleteCookie(CookieKey.SuperadminAccessToken);
    this.authHelpers.deleteCookie(CookieKey.AccessToken);
    this.authHelpers.deleteCookie(CookieKey.Company);
    this.authHelpers.deleteCookie(CookieKey.OrgGuid);
  }

  public saveAccessTokenInCookie(value: string, hasHttps: boolean): void {
    if (this.authHelpers.isSuperadmin) {
      // 0.5 / 24 == 30 minutes
      this.cookieService.doSaveInCookie(CookieKey.SuperadminAccessToken, value, 0.5 / 24);
    } else {
      this.cookieService.doSaveInCookie(CookieKey.AccessToken, value, null, hasHttps);
    }
  }

  public clearAccessToken(): void {
    this.authHelpers.deleteCookie(CookieKey.AccessToken);
  }

  public redirectToSignOut(redirectUrl?: string): void {
    const queryParams = redirectUrl ? `?redirect_to=${encodeURIComponent(redirectUrl)}` : '';
    const authUrl = this.getAuthUrl();
    window.location.replace(`${authUrl}sign-out${queryParams}`);
  }

  public referralsSiteUrl(company: any): string {
    return `${this.getAuthUrl()}c/${company.id}/referrals`;
  }

  // eslint-disable-next-line max-lines-per-function
  public fetchAdmin(
    authToken: string,
    apiUrl: string,
    authMethod: string,
    headers: Record<string, string>,
  ) {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `${authMethod} ${authToken}`,
        [IdentityHeader.Startup]: 'true',
        ...HttpApiService.defaultHeaders,
        ...headers,
      }),
    };

    const preferredCompanyId = this.getPreferredCompanyId();

    return this.http
      .get(`${apiUrl}admin`, {
        ...httpOptions,
        params: {
          ...(preferredCompanyId && { preferred_company_id: `${preferredCompanyId}` }),
        },
      })
      .toPromise()
      .then((data: { admin: CurrentUser }) => {
        const admin = {
          ...data.admin,
          companies: this.decodeCompanyNames(data.admin.companies),
        };
        const { companies, companyId } = admin;
        const currentCompany = companies.find(company => company.id === companyId) || null;
        const locale = this.getLocale(admin, currentCompany);

        setDefaultTimezone(admin.timezone);

        this.authHelpers.clearCompanyCookies();
        if (companyId) {
          this.authHelpers.saveCompanyInCookie(companyId.toString());
        }
        removeQueryParam('authorization_code');
        removeQueryParam('company_id');
        removeQueryParam('company');
        return { companyId, locale, admin };
      })
      .catch(error => {
        this.onResponseError(error);
        return Promise.reject(error);
      });
  }

  public redirectToDashboard(): void {
    if (!window.location.href.includes('/dashboard/overview')) {
      window.location.replace(this.getWebAppUrl() + `#/dashboard/overview`);
    }
  }

  public redirectToSignIn(companyId: number): void {
    window.location.href = `${this.appConfig.authUrl}c/${companyId}/sign-in`;
  }

  public redirectToAuth(redirectUrl?: string): void {
    window.location.replace(
      this.getAuthUrl() +
        `?redirect=${encodeURIComponent(redirectUrl ? redirectUrl : window.location.href)}`,
    );
  }

  public redirectToBlockedAccount(): void {
    this.authHelpers.deleteCookie(CookieKey.AccessToken);
    window.location.replace(
      this.getAuthUrl() +
        `sign-out?redirect_to=${encodeURIComponent(this.getAppUrl() + 'account-blocked')}`,
    );
  }

  public onUnauthorized(error: any): void {
    this.executionStrategy.onUnauthorized(error);
  }

  public onForbidden(error: any, companyId?: number, url?: string): void {
    this.executionStrategy.onForbidden(error, companyId, url);
  }

  public getAuthUrl(): string {
    return this.appConfig.authUrl;
  }

  private getLocale(
    admin: { preferredLanguage: string },
    company: { language: string } | null,
  ): string {
    if (
      admin &&
      admin.preferredLanguage &&
      admin.preferredLanguage !== 'auto' &&
      this.availableLocales.includes(admin.preferredLanguage)
    ) {
      return admin.preferredLanguage;
    } else if (company && company.language && this.availableLocales.includes(company.language)) {
      return company.language;
    }

    return this.defaultLocale;
  }

  private getLocaleFromBrowser(): string {
    let localeFromBrowser;

    if (navigator.languages && navigator.languages.length) {
      localeFromBrowser = navigator.languages.find(l =>
        this.availableLocales.includes(l.substring(0, 2)),
      );
    }

    if (localeFromBrowser) {
      return localeFromBrowser.substring(0, 2);
    }

    return this.defaultLocale;
  }

  private onResponseError(response: any): any {
    if ((!response.ok && response.status === 401) || response.status === 422) {
      this.executionStrategy.onAuthorizationFailed(response);
    }

    return response;
  }

  private tradeAuthorizationCodeForAccessToken(authorizationCode: string): Promise<any> {
    return this.http
      .get(`${this.getAuthUrl()}oauth/tokens/trade/${authorizationCode}`, {
        headers: HttpApiService.defaultHeaders,
      })
      .toPromise();
  }

  private getAccessTokenWithCors(): Promise<any> {
    return this.http
      .get(`${this.getAuthUrl()}oauth/token`, {
        withCredentials: true,
        headers: HttpApiService.defaultHeaders,
      })
      .toPromise()
      .catch(data => this.onResponseError(data));
  }

  private getAppUrl(): string {
    return this.appConfig.appUrl;
  }

  private getWebAppUrl(): string {
    return this.appConfig.webAppUrl;
  }

  private getPreferredCompanyId(): number | null {
    const companyIdFromQuery = getQueryParam('company_id') || getQueryParam('company');
    if (companyIdFromQuery) return parseInt(companyIdFromQuery, 10);

    const companyIdFromCookie = this.cookieService.get(CookieKey.Company);
    if (companyIdFromCookie) return parseInt(companyIdFromCookie, 10);

    return null;
  }

  private decodeCompanyNames(companies: CurrentUser['companies']): CurrentUser['companies'] {
    return companies.map(company => ({
      ...company,
      name: decodeHTML(company.name),
    }));
  }
}
